/**
 * WorkOrderListPage — Issue #345 admin operational table rollout.
 *
 * Backed by the existing useSwo hooks (real backend endpoints). The page now
 * uses the shared AdminDataTable so it picks up advanced search, column
 * visibility, loading/empty/error states and a permission-aware row action
 * menu that drives the work-order state-machine through the real start /
 * block / unblock / complete / cancel endpoints. Cancellation is the
 * non-physical removal semantic exposed by the backend — operational work
 * orders are never physically deleted.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import {
  Ban,
  CheckCircle2,
  Eye,
  PauseCircle,
  Pencil,
  PlayCircle,
} from 'lucide-react';
import {
  useWorkOrderList,
  useWorkOrderTransition,
  WORK_ORDER_PRIORITY_LABELS,
  WORK_ORDER_STATUS_LABELS,
  WORK_ORDER_STATUS_LIST,
  WORK_ORDER_TYPE_LABELS,
  type ServiceWorkOrderDto,
  type WorkOrderStatus,
} from '@/hooks/useSwo';
// Issue #345 audit fix2: 此前的高级搜索包含 `title` 文本输入，但 useWorkOrderList
// 不接受标题查询参数 —— 服务端按 `{ page, limit, status }` 翻页后再前端筛选会
// 导致客户在第 N 页搜索到的命中数和后端 `total` 不一致。在后端补齐工单标题
// 查询能力之前，工具栏只保留状态下拉（服务端真正支持）。
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { SWO_PERMISSIONS, hasPermission } from '@/lib/swoPermissions';
import { Button } from '@/components/ui/button';
import {
  AdminDataTable,
  type AdminDataTableAction,
  type AdminDataTableColumn,
} from '@/components/business/AdminDataTable';

const PRIORITY_BADGE: Record<string, string> = {
  low: 'bg-slate-100 text-slate-700',
  normal: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const STATUS_BADGE: Record<string, string> = {
  open: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-blue-100 text-blue-700',
  blocked: 'bg-red-100 text-red-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

export default function WorkOrderListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { loading, error, fetch } = useWorkOrderList();
  const {
    loading: transitioning,
    start,
    block,
    unblock,
    complete,
    cancel,
  } = useWorkOrderTransition();
  const [workOrders, setWorkOrders] = useState<readonly ServiceWorkOrderDto[]>([]);
  const [total, setTotal] = useState(0);
  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    status: '',
  });
  const [page, setPage] = useState(1);
  const limit = 20;

  const canRead = hasPermission(permissions, SWO_PERMISSIONS.WORK_ORDER_READ);
  const canCreate = hasPermission(permissions, SWO_PERMISSIONS.WORK_ORDER_CREATE);
  const canTransition = hasPermission(
    permissions,
    SWO_PERMISSIONS.WORK_ORDER_TRANSITION,
  );
  const canCancel = hasPermission(
    permissions,
    SWO_PERMISSIONS.WORK_ORDER_CANCEL,
  );
  const canUpdate = hasPermission(
    permissions,
    SWO_PERMISSIONS.WORK_ORDER_UPDATE,
  );

  const statusFilter = (searchValues['status'] ?? '') as WorkOrderStatus | '';

  const refresh = useCallback(async () => {
    const result = await fetch({
      page,
      limit,
      status: statusFilter !== '' ? statusFilter : undefined,
    });
    if (result) {
      setWorkOrders(result.items);
      setTotal(result.total);
    }
  }, [fetch, page, statusFilter]);

  useEffect(() => {
    if (!canRead) return;
    void refresh();
  }, [canRead, refresh]);

  useEffect(() => {
    if (error) toast.error(`加载工单列表失败：${error.message}`);
  }, [error]);

  // Issue #345 audit fix2: 服务端翻页 + 真实 total，禁止在本地数组上再叠加
  // `title` 等文本筛选，否则会导致用户看到的命中条数比 total 少且翻页跳过命中。
  const visibleRows = workOrders;

  const columns: ReadonlyArray<AdminDataTableColumn<ServiceWorkOrderDto>> = useMemo(
    () => [
      {
        key: 'title',
        header: '标题',
        alwaysVisible: true,
        render: (wo) => <span className="font-medium">{wo.title}</span>,
      },
      {
        key: 'type',
        header: '类型',
        render: (wo) => WORK_ORDER_TYPE_LABELS[wo.type] ?? wo.type,
      },
      {
        key: 'status',
        header: '状态',
        render: (wo) => (
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[wo.status] ?? ''}`}
          >
            {WORK_ORDER_STATUS_LABELS[wo.status] ?? wo.status}
          </span>
        ),
      },
      {
        key: 'priority',
        header: '优先级',
        render: (wo) => (
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_BADGE[wo.priority] ?? ''}`}
          >
            {WORK_ORDER_PRIORITY_LABELS[wo.priority] ?? wo.priority}
          </span>
        ),
      },
      {
        key: 'customerId',
        header: '客户 ID',
        defaultHidden: true,
        render: (wo) => <span className="font-mono text-xs">{wo.customerId}</span>,
      },
      {
        key: 'assigneeId',
        header: '负责人 ID',
        defaultHidden: true,
        render: (wo) => (
          <span className="font-mono text-xs">{wo.assigneeId ?? '—'}</span>
        ),
      },
      {
        key: 'dueAt',
        header: '截止日期',
        render: (wo) =>
          wo.dueAt ? new Date(wo.dueAt).toLocaleDateString('zh-CN') : '—',
      },
      {
        key: 'createdAt',
        header: '创建时间',
        render: (wo) => new Date(wo.createdAt).toLocaleDateString('zh-CN'),
      },
    ],
    [],
  );

  const rowActions: ReadonlyArray<AdminDataTableAction<ServiceWorkOrderDto>> = useMemo(
    () => [
      {
        key: 'view',
        label: '查看',
        icon: <Eye className="h-3.5 w-3.5" />,
        onSelect: (wo) => navigate(`/operations/work-orders/${wo.id}`),
      },
      {
        key: 'edit',
        label: '编辑',
        icon: <Pencil className="h-3.5 w-3.5" />,
        visible: () => canUpdate,
        onSelect: (wo) => navigate(`/operations/work-orders/${wo.id}`),
      },
      {
        key: 'start',
        label: '开工',
        icon: <PlayCircle className="h-3.5 w-3.5" />,
        visible: (wo) => canTransition && wo.status === 'open',
        disabled: () => transitioning,
        onSelect: async (wo) => {
          const next = await start(wo.id, wo.version);
          if (next) {
            toast.success(`工单「${wo.title}」已开工`);
            await refresh();
          }
        },
      },
      {
        key: 'block',
        label: '阻塞',
        icon: <PauseCircle className="h-3.5 w-3.5" />,
        visible: (wo) => canTransition && wo.status === 'in_progress',
        disabled: () => transitioning,
        onSelect: async (wo) => {
          const reason = window.prompt('请说明阻塞原因（必填）');
          const trimmed = reason?.trim();
          if (!trimmed) return;
          const next = await block(wo.id, {
            expectedVersion: wo.version,
            blockReason: trimmed,
          });
          if (next) {
            toast.success(`工单「${wo.title}」已阻塞`);
            await refresh();
          }
        },
      },
      {
        key: 'unblock',
        label: '解除阻塞',
        icon: <PlayCircle className="h-3.5 w-3.5" />,
        visible: (wo) => canTransition && wo.status === 'blocked',
        disabled: () => transitioning,
        onSelect: async (wo) => {
          const next = await unblock(wo.id, wo.version);
          if (next) {
            toast.success(`工单「${wo.title}」已解除阻塞`);
            await refresh();
          }
        },
      },
      {
        key: 'complete',
        label: '完成',
        icon: <CheckCircle2 className="h-3.5 w-3.5" />,
        visible: (wo) => canTransition && wo.status === 'in_progress',
        disabled: () => transitioning,
        onSelect: async (wo) => {
          const next = await complete(wo.id, wo.version);
          if (next) {
            toast.success(`工单「${wo.title}」已完成`);
            await refresh();
          }
        },
      },
      {
        key: 'cancel',
        label: '取消（归档）',
        icon: <Ban className="h-3.5 w-3.5" />,
        destructive: true,
        visible: (wo) =>
          canCancel &&
          wo.status !== 'completed' &&
          wo.status !== 'cancelled',
        disabled: () => transitioning,
        onSelect: async (wo) => {
          const reason = window.prompt('请说明取消原因（必填，将写入审计日志）');
          const trimmed = reason?.trim();
          if (!trimmed) return;
          const next = await cancel(wo.id, {
            expectedVersion: wo.version,
            cancelReason: trimmed,
          });
          if (next) {
            toast.success(`工单「${wo.title}」已取消`);
            await refresh();
          }
        },
      },
    ],
    [
      block,
      cancel,
      canCancel,
      canTransition,
      canUpdate,
      complete,
      navigate,
      refresh,
      start,
      transitioning,
      unblock,
    ],
  );

  if (!canRead) {
    return <AccessDenied resource="服务工单" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">服务工单</h2>
        {canCreate && (
          <Button
            type="button"
            onClick={() => navigate('/operations/work-orders/create')}
          >
            新建工单
          </Button>
        )}
      </div>

      <AdminDataTable<ServiceWorkOrderDto>
        testIdPrefix="work-orders"
        columns={columns}
        rows={visibleRows}
        isLoading={loading}
        error={error}
        onRetry={refresh}
        emptyMessage="暂无工单数据"
        filters={{
          fields: [
            {
              kind: 'select',
              name: 'status',
              label: '状态',
              options: WORK_ORDER_STATUS_LIST.map((s) => ({
                value: s,
                label: WORK_ORDER_STATUS_LABELS[s] ?? s,
              })),
            },
          ],
          values: searchValues,
          onSubmit: (next) => {
            setSearchValues(next);
            setPage(1);
          },
          onReset: () => {
            setSearchValues({ status: '' });
            setPage(1);
          },
        }}
        pagination={{
          page,
          pageSize: limit,
          total,
          onPageChange: setPage,
        }}
        rowActions={rowActions}
      />
    </div>
  );
}
